import { useEffect, useState } from 'react';
import { api, useAuth } from '@onvice/portal-shell-api';

interface MyAccessResponse {
  success: boolean;
  data: { modules: Array<{ slug: string }> };
}

// Je Anwender einmal laden; Persona-Wechsel im Test-Cockpit lädt neu.
const cache = new Map<string, Promise<Set<string>>>();

function loadAccess(userId: string): Promise<Set<string>> {
  let p = cache.get(userId);
  if (!p) {
    p = api.get<MyAccessResponse>('/portal/my-access').then((r) => new Set((r.data.modules ?? []).map((m) => m.slug)));
    // Fehlschlag nicht festhalten — der nächste Aufruf versucht es erneut.
    p.catch(() => { cache.delete(userId); });
    cache.set(userId, p);
  }
  return p;
}

export interface ModuleAccess {
  loaded: boolean;
  /** Darf der Anwender dieses Modul öffnen? Solange nicht geladen: nein. */
  has(slug: string): boolean;
}

/**
 * Welche Module darf der Anwender öffnen (/portal/my-access)? Entscheidet, ob ein
 * Eltern-Verweis als Link oder als Text erscheint. Reine Anzeige-Hilfe — das Backend
 * prüft unabhängig davon.
 */
export function useModuleAccess(): ModuleAccess {
  const { user, isAuthenticated } = useAuth();
  const userId = isAuthenticated ? (user?.id ?? null) : null;
  const [state, setState] = useState<{ userId: string | null; slugs: Set<string> | null }>({ userId: null, slugs: null });

  useEffect(() => {
    if (!userId) return;
    let active = true;
    loadAccess(userId)
      .then((slugs) => { if (active) setState({ userId, slugs }); })
      // Ohne Auskunft erscheinen Eltern als Text; der Inhalt bleibt lesbar.
      .catch(() => { if (active) setState({ userId, slugs: new Set() }); });
    return () => { active = false; };
  }, [userId]);

  const slugs = state.userId === userId ? state.slugs : null;
  return { loaded: slugs != null, has: (slug) => slugs?.has(slug) ?? false };
}
